import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

export interface IDeleteSurveyTemplateParams {
	id: string;
	industry: string;
}

export interface IDeleteSurveyTemplateOptions {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
}

/**
 * Admin: Delete a survey template (soft delete).
 * Marks the template as deleted in the system.
 *
 * @param options - Mutation options including success/error callbacks
 * @returns React Query mutation result
 */
export const useDeleteSurveyTemplateMutation = (options: IDeleteSurveyTemplateOptions = {}) => {
	const { onError, onSuccess } = options;
	const userSession = useAdminUserSession();

	return useMutation({
		mutationFn: (params: IDeleteSurveyTemplateParams) => {
			const { id, industry } = params;

			return userSession.webServiceHelper.callAsync<void>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `admin/surveytemplate/${id}/archive`,
					queryParams: {
						industry,
					},
				}),
				'PUT'
			);
		},
		onSuccess: () => {
			// Invalidate relevant queries
			reactQueryClient.invalidateQueries({ queryKey: ['systemSurveyTemplate'] });
			reactQueryClient.invalidateQueries({ queryKey: ['surveyTemplatesByIndustry'] });
			reactQueryClient.invalidateQueries({ queryKey: ['surveyTemplate'] });
			onSuccess?.();
		},
		onError,
	});
};
